/**
 * Shared sector taxonomy for statement-aware heuristic benchmarks.
 *
 * Classification only — statement-specific thresholds live in each
 * statement's benchmark config.
 */

export const HEURISTIC_EFFECTIVE_FROM = '2026-09-09';
export const BENCHMARK_SOURCE_HEURISTIC = 'heuristic';

export const METHODOLOGY_NOTE =
    'Finlytics sector-aware screening benchmark — initial heuristic; compare with company-specific history and peers.';

export const STATUS_LABELS = {
    strong: 'Strong',
    watch: 'Watch',
    weak: 'Requires investigation',
    notApplicable: 'Not applicable',
    insufficientData: 'Insufficient data',
    diagnosticOnly: 'Diagnostic only',
    selfFunded: 'Self-funded',
    externallyFunded: 'Externally funded',
};

export const CANONICAL_SECTORS = {
    itServicesSoftware: {
        label: 'IT Services & Software',
        aliases: ['it', 'information technology', 'software', 'software services', 'it services', 'computer software'],
    },
    internetDigitalPlatforms: {
        label: 'Internet & Digital Platforms',
        aliases: ['internet', 'digital', 'online services', 'e-commerce', 'internet software'],
    },
    fmcgConsumerStaples: {
        label: 'FMCG & Consumer Staples',
        aliases: ['fmcg', 'consumer staples', 'household products', 'food products', 'personal products'],
    },
    consumerDiscretionaryRetail: {
        label: 'Consumer Discretionary & Retail',
        aliases: ['consumer discretionary', 'retail', 'apparel', 'footwear', 'jewellery', 'department stores'],
    },
    consumerDurables: {
        label: 'Consumer Durables',
        aliases: ['consumer durables', 'durables', 'home appliances', 'electronics'],
    },
    automobilesAutoAncillaries: {
        label: 'Automobiles & Auto Ancillaries',
        aliases: ['automobile', 'automobiles', 'auto', 'auto ancillary', 'auto ancillaries', 'tyres'],
    },
    pharmaHealthcare: {
        label: 'Pharma & Healthcare',
        aliases: ['pharma', 'pharmaceutical', 'healthcare', 'hospital', 'diagnostics', 'life sciences'],
    },
    chemicalsSpecialtyChemicals: {
        label: 'Chemicals & Specialty Chemicals',
        aliases: ['chemicals', 'chemical', 'specialty chemicals', 'agro chemicals', 'agrochemicals'],
    },
    metalsMining: {
        label: 'Metals & Mining',
        aliases: ['metals', 'metal', 'mining', 'steel', 'aluminium', 'copper', 'zinc'],
    },
    cementBuildingMaterials: {
        label: 'Cement & Building Materials',
        aliases: ['cement', 'building materials', 'ceramics', 'tiles', 'glass'],
    },
    industrialProductsCapitalGoods: {
        label: 'Industrial Products & Capital Goods',
        aliases: ['industrial products', 'capital goods', 'engineering', 'industrial machinery', 'electrical equipment', 'bearings', 'railway equipment'],
    },
    infrastructureEpcConstruction: {
        label: 'Infrastructure, EPC & Construction',
        aliases: ['infrastructure', 'epc', 'construction', 'engineering construction', 'roads', 'ports'],
    },
    powerUtilities: {
        label: 'Power & Utilities',
        aliases: ['power', 'utilities', 'electric utilities', 'gas utilities', 'water utilities'],
    },
    oilGasFuels: {
        label: 'Oil, Gas & Fuels',
        aliases: ['oil and gas', 'oil & gas', 'refineries', 'refining', 'fuels', 'petroleum', 'gas distribution'],
    },
    telecom: {
        label: 'Telecom',
        aliases: ['telecom', 'telecommunications', 'communication services'],
    },
    realEstateDevelopers: {
        label: 'Real Estate Developers',
        aliases: ['real estate', 'realty', 'real estate developers', 'property developers'],
    },
    reitInvit: {
        label: 'REITs & InvITs',
        aliases: ['reit', 'invIT', 'invit', 'infrastructure investment trust'],
    },
    logisticsTransport: {
        label: 'Logistics & Transport',
        aliases: ['logistics', 'transport', 'shipping', 'ports', 'airlines', 'railways', 'warehousing'],
    },
    mediaEntertainment: {
        label: 'Media & Entertainment',
        aliases: ['media', 'entertainment', 'broadcasting', 'movies', 'gaming'],
    },
    textilesApparel: {
        label: 'Textiles & Apparel',
        aliases: ['textiles', 'textile', 'apparel', 'garments', 'yarn'],
    },
    agricultureFertilizers: {
        label: 'Agriculture, Fertilisers & Sugar',
        aliases: ['fertiliser', 'fertilizer', 'agriculture', 'agri', 'sugar', 'plantation'],
    },
    paperPackaging: {
        label: 'Paper, Packaging & Printing',
        aliases: ['paper', 'packaging', 'printing', 'paper products'],
    },
    financialInstitutions: {
        label: 'Financial Institutions',
        aliases: ['bank', 'banking', 'private sector bank', 'public sector bank', 'nbfc', 'financial services', 'insurance', 'asset management', 'broking', 'finance'],
    },
    otherOperatingBusinesses: {
        label: 'Other Operating Businesses',
        aliases: [],
    },
};

export const SECTOR_ARCHETYPE_BY_ID = {
    itServicesSoftware: 'assetLightServices',
    internetDigitalPlatforms: 'assetLightServices',
    fmcgConsumerStaples: 'defensiveConsumer',
    consumerDurables: 'defensiveConsumer',
    consumerDiscretionaryRetail: 'defensiveConsumer',
    automobilesAutoAncillaries: 'manufacturingIndustrial',
    pharmaHealthcare: 'healthcareChemicals',
    chemicalsSpecialtyChemicals: 'healthcareChemicals',
    metalsMining: 'commodityCyclical',
    cementBuildingMaterials: 'commodityCyclical',
    agricultureFertilizers: 'commodityCyclical',
    oilGasFuels: 'commodityCyclical',
    industrialProductsCapitalGoods: 'manufacturingIndustrial',
    infrastructureEpcConstruction: 'longCycleProject',
    powerUtilities: 'regulatedNetworkCapex',
    telecom: 'regulatedNetworkCapex',
    realEstateDevelopers: 'longCycleProject',
    reitInvit: 'notApplicable',
    logisticsTransport: 'manufacturingIndustrial',
    mediaEntertainment: 'media',
    textilesApparel: 'manufacturingIndustrial',
    paperPackaging: 'manufacturingIndustrial',
    financialInstitutions: 'notApplicable',
    otherOperatingBusinesses: 'manufacturingIndustrial',
};

export const ARCHETYPE_LABELS = {
    assetLightServices: 'Asset-light services',
    defensiveConsumer: 'Defensive consumer',
    healthcareChemicals: 'Healthcare / chemicals',
    manufacturingIndustrial: 'Manufacturing / industrial',
    commodityCyclical: 'Commodity / cyclical',
    longCycleProject: 'Long-cycle project',
    regulatedNetworkCapex: 'Regulated / network capex',
    media: 'Media',
    notApplicable: 'Not applicable',
};

export const HISTORICAL_WINDOW_BY_ARCHETYPE = {
    assetLightServices: 3,
    defensiveConsumer: 3,
    healthcareChemicals: 3,
    manufacturingIndustrial: 3,
    commodityCyclical: 5,
    longCycleProject: 5,
    regulatedNetworkCapex: 3,
    media: 3,
    notApplicable: 3,
};

export const NOT_APPLICABLE_ARCHETYPES = new Set(['financialInstitutions', 'reitInvit']);

export const NOT_APPLICABLE_REASONS = {
    financialInstitutions:
        'Banks, NBFCs, insurers, brokers, and asset managers require sector-specific measures such as NIM, credit cost, capital adequacy, solvency, AUM flows, and regulatory capital.',
    reitInvit:
        'Reported FCF is not a primary decision metric for REITs and InvITs. AFFO/FFO, distributable cash flow, leverage, occupancy/traffic, and distribution coverage are more appropriate.',
};

export const FALLBACK_SECTOR_NOTE =
    'Source sector did not match a canonical Finlytics mapping; Other Operating Businesses / manufacturing-industrial thresholds are used as a fallback.';
